import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, mediaUrl, formatApiError } from "@/lib/api";
import { eur } from "@/context/CartContext";
import { Package, Plus, Pencil, Trash2, Loader2, X, Upload, RefreshCw } from "lucide-react";

const EMPTY = { name: "", price_eur: "", description: "", image_url: "", category: "accessory", stock: "", active: true, sort_order: 0 };

export default function AdminProducts() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // product or {} for new
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/products");
            setProducts(data);
        } catch (e) {
            toast.error("Falhou: " + formatApiError(e?.response?.data?.detail || e.message));
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); }, []);

    function openNew() { setForm(EMPTY); setEditing({}); }
    function openEdit(p) {
        setForm({ name: p.name, price_eur: (p.price_cents / 100).toString(), description: p.description || "", image_url: p.image_url || "", category: p.category, stock: p.stock == null ? "" : String(p.stock), active: p.active, sort_order: p.sort_order || 0 });
        setEditing(p);
    }

    async function uploadImage(file) {
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/cms/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setForm((s) => ({ ...s, image_url: data.url }));
            toast.success("Imagem carregada.");
        } catch (e) {
            toast.error("Falhou o upload: " + formatApiError(e?.response?.data?.detail || e.message));
        } finally {
            setUploading(false);
        }
    }

    async function save() {
        if (!form.name.trim()) { toast.error("Indica o nome."); return; }
        const payload = {
            name: form.name.trim(),
            price_eur: parseFloat(form.price_eur || "0"),
            description: form.description,
            image_url: form.image_url,
            category: form.category,
            stock: form.stock === "" ? null : parseInt(form.stock, 10),
            active: form.active,
            sort_order: parseInt(form.sort_order || "0", 10),
        };
        setSaving(true);
        try {
            if (editing && editing.id) await api.put(`/admin/products/${editing.id}`, payload);
            else await api.post("/admin/products", payload);
            toast.success("Produto guardado.");
            setEditing(null);
            load();
        } catch (e) {
            toast.error("Falhou: " + formatApiError(e?.response?.data?.detail || e.message));
        } finally {
            setSaving(false);
        }
    }

    async function del(p) {
        if (!window.confirm(`Eliminar "${p.name}"?`)) return;
        try {
            await api.delete(`/admin/products/${p.id}`);
            toast.success("Produto eliminado.");
            load();
        } catch (e) {
            toast.error("Falhou: " + formatApiError(e?.response?.data?.detail || e.message));
        }
    }

    return (
        <div className="space-y-6" data-testid="admin-products">
            <header className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="font-heading text-xl lg:text-2xl font-extrabold text-primary inline-flex items-center gap-2"><Package className="h-5 w-5" /> Produtos / Loja</h1>
                    <p className="text-muted-foreground mt-1">Gere os produtos visíveis na loja pública.</p>
                </div>
                <div className="flex gap-2">
                    <button onClick={load} className="inline-flex items-center gap-2 rounded-md border border-border bg-white px-4 py-2 text-sm font-semibold text-primary hover:bg-muted"><RefreshCw className="h-4 w-4" /> Recarregar</button>
                    <button onClick={openNew} data-testid="new-product-btn" className="inline-flex items-center gap-2 rounded-md bg-secondary px-4 py-2 text-sm font-bold text-secondary-foreground"><Plus className="h-4 w-4" /> Novo produto</button>
                </div>
            </header>

            {loading ? (
                <div className="py-20 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
            ) : products.length === 0 ? (
                <div className="rounded-2xl border border-border bg-white p-10 text-center text-muted-foreground">Sem produtos. Cria o primeiro.</div>
            ) : (
                <div className="rounded-2xl border border-border bg-white overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                            <tr>
                                <th className="px-4 py-3">Produto</th>
                                <th className="px-4 py-3">Categoria</th>
                                <th className="px-4 py-3">Preço</th>
                                <th className="px-4 py-3">Stock</th>
                                <th className="px-4 py-3">Estado</th>
                                <th className="px-4 py-3 text-right">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {products.map((p) => (
                                <tr key={p.id} data-testid={`product-row-${p.slug}`} className="hover:bg-muted/30">
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-3">
                                            <div className="h-10 w-10 rounded bg-muted overflow-hidden flex-shrink-0">{p.image_url ? <img src={mediaUrl(p.image_url)} alt="" className="h-full w-full object-cover" /> : null}</div>
                                            <span className="font-semibold text-primary">{p.name}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-muted-foreground">{p.category === "model" ? "Modelo" : "Acessório"}</td>
                                    <td className="px-4 py-3 font-semibold text-primary">{eur(p.price_cents)}</td>
                                    <td className="px-4 py-3 text-muted-foreground">{p.stock == null ? "∞" : p.stock}</td>
                                    <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-xs font-bold ${p.active ? "bg-emerald-50 text-emerald-600" : "bg-gray-100 text-gray-500"}`}>{p.active ? "Ativo" : "Inativo"}</span></td>
                                    <td className="px-4 py-3">
                                        <div className="flex justify-end gap-1">
                                            <button onClick={() => openEdit(p)} className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-muted text-primary"><Pencil className="h-4 w-4" /></button>
                                            <button onClick={() => del(p)} className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-red-50 text-red-500"><Trash2 className="h-4 w-4" /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {editing && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setEditing(null)} />
                    <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-white">
                            <h2 className="font-heading text-lg font-bold text-primary">{editing.id ? "Editar produto" : "Novo produto"}</h2>
                            <button onClick={() => setEditing(null)} className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-muted"><X className="h-5 w-5" /></button>
                        </div>
                        <div className="px-6 py-5 space-y-4">
                            <Field label="Nome"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name" className="inp" /></Field>
                            <div className="grid grid-cols-2 gap-4">
                                <Field label="Preço (€)"><input type="number" step="0.01" min="0" value={form.price_eur} onChange={(e) => setForm({ ...form, price_eur: e.target.value })} data-testid="product-price" className="inp" /></Field>
                                <Field label="Categoria">
                                    <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="product-category" className="inp">
                                        <option value="model">Modelo</option>
                                        <option value="accessory">Acessório</option>
                                    </select>
                                </Field>
                            </div>
                            <Field label="Descrição"><textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="inp" /></Field>
                            <Field label="Imagem">
                                <div className="flex items-center gap-3">
                                    <div className="h-16 w-16 rounded bg-muted overflow-hidden flex-shrink-0">{form.image_url ? <img src={mediaUrl(form.image_url)} alt="" className="h-full w-full object-cover" /> : null}</div>
                                    <label className="inline-flex items-center gap-2 rounded-md border border-border bg-white px-3 py-2 text-sm font-semibold text-primary cursor-pointer hover:bg-muted">
                                        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Carregar
                                        <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadImage(e.target.files[0])} />
                                    </label>
                                </div>
                            </Field>
                            <div className="grid grid-cols-2 gap-4">
                                <Field label="Stock (vazio = ilimitado)"><input type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="inp" /></Field>
                                <Field label="Ordem"><input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className="inp" /></Field>
                            </div>
                            <label className="flex items-center gap-2 text-sm font-semibold text-primary">
                                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="product-active" /> Ativo (visível na loja)
                            </label>
                        </div>
                        <div className="px-6 py-4 border-t border-border flex justify-end gap-2 sticky bottom-0 bg-white">
                            <button onClick={() => setEditing(null)} className="rounded-md border border-border px-4 py-2 text-sm font-semibold text-primary hover:bg-muted">Cancelar</button>
                            <button onClick={save} disabled={saving} data-testid="product-save" className="inline-flex items-center gap-2 rounded-md bg-secondary px-5 py-2 text-sm font-bold text-secondary-foreground disabled:opacity-50">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Guardar</button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`.inp{width:100%;border:1px solid hsl(var(--border));border-radius:0.375rem;padding:0.5rem 0.75rem;font-size:0.875rem;color:hsl(var(--primary));background:white}.inp:focus{outline:none;box-shadow:0 0 0 2px hsl(var(--secondary))}`}</style>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-muted-foreground mb-1.5">{label}</label>
            {children}
        </div>
    );
}
